import { checklistService, Checklist, ChecklistItem, EvidenceFieldDefinition } from '@/firebase/checklistService';
import { checklistCompletionService, ChecklistCompletion, ChecklistItemResult } from '@/firebase/checklistCompletionService';
import { checklistEvidenceService } from '@/firebase/checklistEvidenceService';
import { assetService, Asset } from '@/firebase/assetService';
import { assetFactsService } from '@/firebase/assetFactsService';
import { assetFileService, AssetFile } from '@/firebase/assetFileService';
import { extractFieldValuesFromDocument, evaluateChecklistItemWithAI, ChecklistItemEvaluationResult, fileNameMatchesKey, getFileNameKeys, fileNameMatchesAnyKey } from '@/services/geminiService';
import { getRequiredFieldsForAssetFacts } from '@/services/reprocessAssetFactsService';
import { getIndustryType } from '@/firebase/organizationService';
import { Organization } from '@/firebase/organizationService';
import { AssetFact } from '@/firebase/assetFactsService';
import { ref, getBytes } from 'firebase/storage';
import { storage } from '@/firebase/config';
import { validateItemCompletion, evaluateAutoCompleteRules } from '@/services/checklistValidationService';

/**
 * Download a file from Firebase Storage and convert to File object
 * Uses assetFileService.downloadAssetFile() which handles Enterprise decryption automatically
 */
async function downloadFileFromStorage(assetFile: AssetFile, organizationId: string): Promise<File | null> {
  try {
    if (!assetFile.id) {
      console.warn(`File ${assetFile.fileName} missing ID`);
      return null;
    }

    // Use assetFileService.downloadAssetFile() which handles Enterprise decryption
    const downloadResult = await assetFileService.downloadAssetFile(assetFile.id, organizationId);
    
    if (!downloadResult.success || !downloadResult.data) {
      console.error(`Error downloading file ${assetFile.fileName}:`, downloadResult.error);
      return null;
    }

    return downloadResult.data;
  } catch (error: any) {
    console.error(`Error downloading file ${assetFile.fileName}:`, error);
    throw error;
  }
}

/**
 * Extracted evidence field value with metadata
 */
export interface ExtractedFieldValue {
  value: any;
  confidence?: 'high' | 'medium' | 'low';
  source?: string;
  allValues?: Array<{
    value: any;
    fileName: string;
    confidence?: 'high' | 'medium' | 'low';
  }>;
  missing?: boolean;
  error?: string;
}

/**
 * Extracted evidence fields organized by evidence name
 */
export interface ExtractedEvidenceFields {
  [evidenceName: string]: {
    [fieldKey: string]: ExtractedFieldValue;
  };
}

/**
 * Extract evidence fields from asset facts using EXACT key matching
 * Asset facts keys MUST exactly match evidence field keys (after normalization)
 * 
 * @param item - Checklist item with evidenceDefinitions
 * @param facts - Asset facts (keys should match evidence field keys exactly)
 * @returns Extracted evidence fields organized by category
 */
export function extractEvidenceFieldsForItem(
  item: ChecklistItem,
  facts: Record<string, AssetFact>
): ExtractedEvidenceFields {
  const extracted: ExtractedEvidenceFields = {};
  
  console.log(`[extractEvidenceFieldsForItem] Processing item: ${item.text}, has ${item.evidenceDefinitions?.length || 0} evidence definitions`);
  
  // Iterate through each evidence definition
  for (const evidenceDef of item.evidenceDefinitions || []) {
    // Try evidenceName first, then categoryCode (legacy), then fallback
    const evidenceName = evidenceDef.evidenceName || (evidenceDef as any).categoryCode || 'Unknown Evidence';
    if (!extracted[evidenceName]) {
      extracted[evidenceName] = {};
    }
    console.log(`[extractEvidenceFieldsForItem] Processing evidence: ${evidenceName}, evidenceDef keys: ${Object.keys(evidenceDef).join(', ')}, has evidenceName: ${!!evidenceDef.evidenceName}, has categoryCode: ${!!(evidenceDef as any).categoryCode}, evidenceName value: ${evidenceDef.evidenceName || 'undefined'}, categoryCode value: ${(evidenceDef as any).categoryCode || 'undefined'}`);
    
    // For each field definition, extract value using EXACT key match
    for (const fieldDef of evidenceDef.fieldDefinitions || []) {
      const fieldKey = fieldDef.key; // e.g., "isBusinessPurposeCertified"
      
      // CRITICAL: Use EXACT key matching - no fuzzy matching, no normalization
      // Asset facts should already have normalized keys matching field definitions
      const fact = facts[fieldKey];
      
      // Fields with workflow rules are derived only from workflow (AI), not from file extraction - skip fact so workflow wins
      const isWorkflowOnlyField = !!(fieldDef.workflowRules && fieldDef.workflowRules.trim());
      
      // Priority: REVIEWER_OVERRIDE > extractedValues > fact.value
      let fieldValue: any = null;
      let fieldSource: string | undefined = undefined;
      let fieldConfidence: 'high' | 'medium' | 'low' = 'medium';
      const allValues: Array<{ value: any; fileName: string; confidence?: 'high' | 'medium' | 'low' }> = [];
      
      // Helper to check if a value is considered "empty"
      const isEmptyValue = (val: any): boolean => {
        if (val === null || val === undefined) return true;
        if (val === '') return true;
        if (typeof val === 'string' && val.trim() === '') return true;
        // Date objects and Firestore Timestamps are never empty
        if (val instanceof Date) return false;
        if (val && typeof val.toDate === 'function') return false; // Firestore Timestamp
        if (typeof val === 'object' && (val.seconds !== undefined || val._seconds !== undefined)) return false; // Timestamp-like
        return false;
      };
      
      if (fact && !isWorkflowOnlyField) {
        if (fact.source === 'REVIEWER_OVERRIDE' && !isEmptyValue(fact.value)) {
          // Reviewer override takes highest priority
          fieldValue = fact.value;
          fieldSource = fact.extractedFrom || 'Manually Set';
          fieldConfidence = 'high'; // Reviewer overrides are always high confidence
          // Include override value in allValues
          allValues.push({
            value: fact.value,
            fileName: fact.extractedFrom || 'Manually Set',
            confidence: 'high',
          });
        } else if (fact.extractedValues && fact.extractedValues.length > 0) {
          // Filter by fileNameKey/fileNameKeys if specified
          // This ensures we only use values from files matching the fileNameKey pattern(s)
          let matchingExtractedValues = fact.extractedValues;
          
          const fileNameKeys = getFileNameKeys(fieldDef);
          if (fileNameKeys.length > 0) {
            // Filter to only values from files matching any of the fileNameKeys
            matchingExtractedValues = fact.extractedValues.filter((ev: any) => {
              if (!ev.fileName) return false;
              return fileNameMatchesAnyKey(ev.fileName, fileNameKeys);
            });
            
            // If no matching values found but fileNameKeys are specified, log a warning
            // but fall back to all values for backward compatibility
            if (matchingExtractedValues.length === 0) {
              console.debug(
                `[extractEvidenceFieldsForItem] Field "${fieldDef.label || fieldKey}" has fileNameKeys [${fileNameKeys.join(', ')}] ` +
                `but no extracted values match these patterns. Available files: ${fact.extractedValues.map((ev: any) => ev.fileName).join(', ')}. ` +
                `Falling back to all values for backward compatibility.`
              );
              // Fall back to all values if no matches found
              matchingExtractedValues = fact.extractedValues;
            } else {
              console.log(
                `[extractEvidenceFieldsForItem] Filtered ${fact.extractedValues.length} extracted values to ${matchingExtractedValues.length} ` +
                `matching fileNameKeys [${fileNameKeys.join(', ')}] for field "${fieldDef.label || fieldKey}"`
              );
            }
          }
          
          // Use filtered extracted values from files
          matchingExtractedValues.forEach((ev: any) => {
            if (!isEmptyValue(ev.value)) {
              allValues.push({
                value: ev.value,
                fileName: ev.fileName,
                confidence: ev.confidence,
              });
            }
          });
          
          // Use primary value (highest confidence from matching files)
          if (allValues.length > 0) {
            const sorted = allValues.sort((a: any, b: any) => {
              const confMap: Record<string, number> = { high: 3, medium: 2, low: 1 };
              return (confMap[b.confidence || 'low'] || 0) - (confMap[a.confidence || 'low'] || 0);
            });
            fieldValue = sorted[0].value;
            fieldSource = sorted[0].fileName || fact.extractedFrom || 'Asset Facts';
            fieldConfidence = sorted[0].confidence || fact.confidence || 'medium';
          }
        } else if (!isEmptyValue(fact.value)) {
          // Legacy: single extracted value
          fieldValue = fact.value;
          fieldSource = fact.extractedFrom || 'Asset Facts';
          fieldConfidence = typeof fact.confidence === 'string' ? fact.confidence : 'medium';
          if (fact.extractedFrom) {
            allValues.push({
              value: fact.value,
              fileName: fact.extractedFrom,
              confidence: fieldConfidence,
            });
          }
        }
      }
      
      // Workflow-derived values are set by AI in re-evaluate/reprocess (evaluateFieldWorkflowRuleWithAI), not here.

      if (!isEmptyValue(fieldValue)) {
        extracted[evidenceName][fieldKey] = {
          value: fieldValue,
          confidence: fieldConfidence,
          source: fieldSource,
          allValues: allValues.length > 0 ? allValues : undefined,
        };
        console.log(`[extractEvidenceFieldsForItem] Found field: ${fieldDef.label} (key: ${fieldKey}), value: ${JSON.stringify(fieldValue)}, source: ${fieldSource}, confidence: ${fieldConfidence}`);
      } else {
        // Field not found - mark as missing
        // This is expected if:
        // 1. The field was recently added to the checklist but documents haven't been reprocessed
        // 2. The documents don't contain information for this field
        // Only log warnings for required fields to reduce noise
        const isRequired = evidenceDef.required && fieldDef.required;
        if (!fact && isRequired) {
          console.debug(
            `[Evidence Extraction] Required field "${fieldDef.label || fieldKey}" (key: "${fieldKey}") not found in asset facts. ` +
            `This is expected if documents haven't been reprocessed since this field was added to the checklist. ` +
            `Please reprocess documents to extract this field.`
          );
        }
        extracted[evidenceName][fieldKey] = {
          value: null,
          confidence: 'low',
          source: undefined,
          missing: true,
          error: fact ? 'Value is null or empty' : `Key "${fieldKey}" not found in asset facts. Documents may need to be reprocessed.`,
        };
        console.log(`[extractEvidenceFieldsForItem] Missing field: ${fieldDef.label} (key: ${fieldKey}), fact exists: ${!!fact}, fact value: ${fact ? JSON.stringify(fact.value) : 'N/A'}, fact source: ${fact ? fact.source : 'N/A'}`);
      }
    }
  }
  
  return extracted;
}

/**
 * Get the primary value from an asset fact (value or first extracted value).
 */
function getFactValue(facts: Record<string, AssetFact | { value?: any }>, key: string): any {
  const fact = facts[key];
  if (!fact) return undefined;
  const v = (fact as AssetFact).value;
  if (v !== null && v !== undefined && v !== '') return v;
  const ev = (fact as AssetFact).extractedValues;
  if (ev && ev.length > 0 && ev[0].value !== null && ev[0].value !== undefined && ev[0].value !== '') {
    return ev[0].value;
  }
  return undefined;
}

/**
 * Evaluate a workflow-only evidence (no field definitions) against asset facts.
 * Parses simple workflow rules like "if collateral code is 63 then it's first lien"
 * and returns true if the condition is satisfied.
 */
export function evaluateWorkflowOnlyEvidence(
  workflowRules: string,
  facts: Record<string, AssetFact | { value?: any }>
): boolean {
  if (!workflowRules || typeof workflowRules !== 'string') return false;
  const rule = workflowRules.trim();
  let factKey = 'collateralCode';
  let expectedVal: number | null = null;
  // Match "collateral code is 63" or "collateralCode is 63"
  const collateralMatch = rule.match(/(?:collateral\s*code|collateralCode)\s+is\s+(\d+)/i);
  if (collateralMatch) {
    factKey = 'collateralCode';
    expectedVal = parseInt(collateralMatch[1], 10);
  } else {
    const genericMatch = rule.match(/(\w+)\s+is\s+(\d+)/);
    if (genericMatch) {
      factKey = genericMatch[1];
      expectedVal = parseInt(genericMatch[2], 10);
    }
  }
  if (expectedVal == null || isNaN(expectedVal)) return false;
  const actual = getFactValue(facts, factKey) ?? getFactValue(facts, 'collateralCode');
  if (actual === undefined || actual === null) return false;
  const actualNum = typeof actual === 'number' ? actual : parseInt(String(actual), 10);
  if (!isNaN(actualNum)) return actualNum === expectedVal;
  return String(actual).trim() === String(expectedVal);
}

/**
 * Evaluate a checklist item based on asset facts
 * Returns evaluation result if facts can determine the item status, null otherwise
 * 
 * @deprecated This function uses hardcoded logic. Use AI-based evaluation instead.
 * Kept for backward compatibility and fallback scenarios.
 */
export function evaluateItemBasedOnFacts(
  item: ChecklistItem,
  facts: Record<string, AssetFact>
): { status: 'PASS' | 'FAIL' | 'NA'; findingText: string } | null {
  const itemText = item.text.toLowerCase();
  
  // Flood-related items
  if (itemText.includes('flood') || itemText.includes('sfha')) {
    const isSFHA = facts['isSFHA']?.value;
    const floodZone = facts['zone']?.value;
    
    // If property is not in SFHA, flood insurance items can be marked as NA
    if (itemText.includes('flood insurance') || itemText.includes('flood notice')) {
      if (isSFHA === false) {
        return {
          status: 'NA',
          findingText: 'Property is not in SFHA - flood insurance not required',
        };
      } else if (isSFHA === true && floodZone) {
        return {
          status: 'PASS',
          findingText: `Property is in SFHA (Zone ${floodZone}) - flood insurance required`,
        };
      }
    }
    
    // Flood determination items
    if (itemText.includes('flood determination') || itemText.includes('flood zone')) {
      if (floodZone) {
        return {
          status: 'PASS',
          findingText: `Flood zone determined: ${floodZone}`,
        };
      }
    }
  }
  
  // Business purpose / Joint intent items
  if (itemText.includes('business purpose') || itemText.includes('joint intent')) {
    // Check for isBusinessPurposeCertified field (from evidence definitions)
    const isBusinessPurposeCertified = facts['isBusinessPurposeCertified']?.value;
    // Also check legacy field name for backward compatibility
    const businessPurpose = facts['businessPurpose']?.value;
    const dwellingSecured = facts['dwellingSecured']?.value;
    
    // Helper function to normalize boolean/string values
    const normalizeBoolean = (value: any): boolean | null => {
      if (value === true || value === 'true' || value === 'True' || value === 'yes' || value === 'Yes' || value === 'YES') {
        return true;
      }
      if (value === false || value === 'false' || value === 'False' || value === 'no' || value === 'No' || value === 'NO') {
        return false;
      }
      return null;
    };
    
    if (itemText.includes('business purpose')) {
      // Check isBusinessPurposeCertified first (from evidence fields)
      const businessPurposeValue = isBusinessPurposeCertified !== undefined ? normalizeBoolean(isBusinessPurposeCertified) : normalizeBoolean(businessPurpose);
      
      if (businessPurposeValue === true) {
        return {
          status: 'PASS',
          findingText: 'Loan is for business purpose',
        };
      } else if (businessPurposeValue === false) {
        return {
          status: 'NA',
          findingText: 'Loan is not for business purpose',
        };
      }
    }
    
    if (itemText.includes('joint intent') || itemText.includes('joint intent')) {
      // Joint intent is typically required for dwelling-secured loans
      if (dwellingSecured === true) {
        return {
          status: 'PASS',
          findingText: 'Loan is secured by dwelling - joint intent required',
        };
      } else if (dwellingSecured === false) {
        return {
          status: 'NA',
          findingText: 'Loan is not secured by dwelling - joint intent not required',
        };
      }
    }
  }
  
  // CRA-related items
  if (itemText.includes('cra') || itemText.includes('small business')) {
    const isSmallBusiness = facts['isSmallBusiness']?.value;
    const isAgLoan = facts['isAgLoan']?.value;
    
    if (itemText.includes('small business')) {
      if (isSmallBusiness === true) {
        return {
          status: 'PASS',
          findingText: 'Loan qualifies as small business loan',
        };
      } else if (isSmallBusiness === false) {
        return {
          status: 'NA',
          findingText: 'Loan does not qualify as small business loan',
        };
      }
    }
    
    if (itemText.includes('ag') || itemText.includes('agricultural')) {
      if (isAgLoan === true) {
        return {
          status: 'PASS',
          findingText: 'Loan qualifies as agricultural loan',
        };
      } else if (isAgLoan === false) {
        return {
          status: 'NA',
          findingText: 'Loan does not qualify as agricultural loan',
        };
      }
    }
  }
  
  // HMDA-related items
  if (itemText.includes('hmda')) {
    const isReportable = facts['isReportable']?.value;
    
    if (isReportable === true) {
      return {
        status: 'PASS',
        findingText: 'Loan is HMDA reportable',
      };
    } else if (isReportable === false) {
      return {
        status: 'NA',
        findingText: 'Loan is not HMDA reportable',
      };
    }
  }
  
  // Collateral-related items
  if (itemText.includes('collateral') || itemText.includes('improved real estate')) {
    const isImprovedRealEstate = facts['isImprovedRealEstate']?.value;
    
    if (isImprovedRealEstate === true) {
      return {
        status: 'PASS',
        findingText: 'Collateral is improved real estate',
      };
    } else if (isImprovedRealEstate === false) {
      return {
        status: 'NA',
        findingText: 'Collateral is not improved real estate',
      };
    }
  }
  
  // Date-based items (e.g., closing date, application date)
  if (itemText.includes('closing date') || itemText.includes('application date')) {
    const closingDate = facts['closingDate']?.value;
    const applicationDate = facts['applicationDate']?.value;
    
    if (itemText.includes('closing date') && closingDate) {
      return {
        status: 'PASS',
        findingText: `Closing date: ${closingDate}`,
      };
    }
    
    if (itemText.includes('application date') && applicationDate) {
      return {
        status: 'PASS',
        findingText: `Application date: ${applicationDate}`,
      };
    }
  }
  
  // No fact-based evaluation available
  return null;
}

export interface AutoCompleteResult {
  success: boolean;
  checklistsProcessed: number;
  itemsCompleted: number;
  evidenceCreated: number;
  factsExtracted: number;
  errors?: string[];
}

/**
 * Auto-complete checklists for an asset based on uploaded documents
 * 
 * This function:
 * 1. Gets all checklists associated with the asset
 * 2. Analyzes uploaded documents
 * 3. Extracts facts (for Bank organizations)
 * 4. Matches documents to evidence requirements
 * 5. Extracts field values from documents
 * 6. Auto-completes checklist items when evidence is found
 * 7. Creates checklist completion records
 */
export async function autoCompleteChecklistsForAsset(
  assetId: string,
  organizationId: string,
  organization: Organization,
  userId: string
): Promise<AutoCompleteResult> {
  const result: AutoCompleteResult = {
    success: true,
    checklistsProcessed: 0,
    itemsCompleted: 0,
    evidenceCreated: 0,
    factsExtracted: 0,
    errors: [],
  };

  try {
    // 1. Get the asset
    const assetResult = await assetService.getAsset(assetId);
    if (!assetResult.success || !assetResult.data) {
      result.success = false;
      result.errors?.push(`Failed to get asset: ${assetResult.error}`);
      return result;
    }

    const asset = assetResult.data;

    // 2. Get all uploaded files for this asset
    const filesResult = await assetFileService.getAssetFiles(assetId, organizationId);
    if (!filesResult.success || !filesResult.data || filesResult.data.length === 0) {
      result.errors?.push('No files found for asset');
      return result;
    }

    const files = filesResult.data;
    const organizationType = getIndustryType(organization);
    const isBankOrganization = organizationType === 'Bank';

    // 3. Download files and convert to File objects for AI processing
    const fileObjects: File[] = [];
    const fileIdMap = new Map<string, string>(); // Map file name to file ID

    for (const assetFile of files) {
      // Accept files that have a URL and are not in error state
      // Use storageUrl (from AssetFile interface) or fileUrl (if it exists)
      const fileUrl = assetFile.storageUrl || (assetFile as any).fileUrl;
      const hasValidStatus = assetFile.status !== 'error';
      
      if (fileUrl && hasValidStatus && assetFile.storagePath) {
        try {
          // Download file from Firebase Storage (handles Enterprise decryption automatically)
          const file = await downloadFileFromStorage(assetFile, organizationId);
          if (file) {
            fileObjects.push(file);
            fileIdMap.set(file.name, assetFile.id!);
          }
        } catch (error: any) {
          console.error(`Error downloading file ${assetFile.fileName}:`, error);
          result.errors?.push(`Failed to download ${assetFile.fileName}: ${error.message}`);
        }
      } else {
        console.warn(`Skipping file ${assetFile.fileName}:`, {
          hasUrl: !!fileUrl,
          status: assetFile.status,
          hasStoragePath: !!assetFile.storagePath
        });
      }
    }

    if (fileObjects.length === 0) {
      result.errors?.push('No downloadable files found for asset');
      return result;
    }

    // 4. Extract field values from all documents using checklist field definitions
    // Get field definitions from checklists for this asset
    const fieldsResult = await getRequiredFieldsForAssetFacts(assetId, organizationId);
    
    if (fieldsResult.success && fieldsResult.requiredFields && fieldsResult.requiredFields.length > 0) {
      for (let i = 0; i < fileObjects.length; i++) {
        const file = fileObjects[i];
        
        // Add delay between files to avoid rate limiting (3 seconds between files)
        if (i > 0) {
          await new Promise(resolve => setTimeout(resolve, 3000));
        }
        
        try {
          const fieldResult = await extractFieldValuesFromDocument(file, fieldsResult.requiredFields!);
          
          if (fieldResult.fieldValues && Object.keys(fieldResult.fieldValues).length > 0) {
            // Transform field values to include source and metadata
            const factsWithSource: Record<string, { 
              value: any; 
              source: 'EXTRACTED'; 
              extractedFrom?: string; 
              confidence?: 'high' | 'medium' | 'low' 
            }> = {};
            
            Object.entries(fieldResult.fieldValues).forEach(([key, value]) => {
              if (value !== null && value !== undefined) {
                factsWithSource[key] = {
                  value,
                  source: 'EXTRACTED',
                  extractedFrom: file.name, // Use file name, not file ID
                  confidence: fieldResult.confidence || 'medium',
                };
              }
            });
            
            // Save facts to asset
            if (Object.keys(factsWithSource).length > 0) {
              const saveResult = await assetFactsService.setFacts(
                assetId,
                factsWithSource,
                organizationId
              );
              if (saveResult.success) {
                result.factsExtracted += Object.keys(factsWithSource).length;
              }
            }
          }
        } catch (error: any) {
          console.error('Error extracting field values from file:', error);
          
          // Handle rate limiting gracefully
          if (error.message?.includes('429') || error.message?.includes('Resource exhausted')) {
            result.errors?.push(`Rate limit reached while processing ${file.name}. Please try again later.`);
            // Stop processing remaining files if we hit rate limit
            break;
          } else {
            result.errors?.push(`Failed to extract field values from ${file.name}: ${error.message}`);
          }
        }
      }
    } else {
      // No field definitions found - this is OK, just means no checklists are configured yet
      console.log('No field definitions found for this asset. Checklists may not be configured with evidence fields yet.');
    }

    // 5. Get all checklists for this asset
    const checklistsResult = await checklistService.getAllChecklists(organizationId);
    if (!checklistsResult.success || !checklistsResult.data) {
      result.success = false;
      result.errors?.push(`Failed to get checklists: ${checklistsResult.error}`);
      return result;
    }

    // Filter checklists that match this asset
    const relevantChecklists = checklistsResult.data.filter(checklist => {
      // Check if checklist is for this asset type
      if (checklist.assetTypeIds && checklist.assetTypeIds.length > 0) {
        if (!asset.assetTypeId || !checklist.assetTypeIds.includes(asset.assetTypeId)) {
          return false;
        }
      }

      // Check if checklist is for this location
      if (checklist.locationIds && checklist.locationIds.length > 0) {
        if (!asset.locationId || !checklist.locationIds.includes(asset.locationId)) {
          return false;
        }
      }

      return true;
    });

    if (relevantChecklists.length === 0) {
      result.errors?.push('No relevant checklists found for this asset');
      return result;
    }

    // 6. Process each checklist with downloaded files
    for (const checklist of relevantChecklists) {
      try {
        const checklistResult = await processChecklistWithFiles(
          checklist,
          asset,
          fileObjects,
          organizationId,
          userId,
          false, // isBankOrganization - TODO: determine from organization
          organization,
          fileIdMap
        );

        result.checklistsProcessed++;
        result.itemsCompleted += checklistResult.itemsCompleted;
        result.evidenceCreated += checklistResult.evidenceCreated;
        if (checklistResult.errors) {
          result.errors?.push(...checklistResult.errors);
        }
      } catch (error: any) {
        console.error(`Error processing checklist ${checklist.id}:`, error);
        result.errors?.push(`Failed to process checklist "${checklist.name}": ${error.message}`);
      }
    }

    return result;
  } catch (error: any) {
    console.error('Error auto-completing checklists:', error);
    result.success = false;
    result.errors?.push(`Fatal error: ${error.message}`);
    return result;
  }
}

interface ProcessChecklistResult {
  itemsCompleted: number;
  evidenceCreated: number;
  errors?: string[];
}

/**
 * Process a single checklist and auto-complete items based on uploaded documents
 */
async function processChecklist(
  checklist: Checklist,
  asset: Asset,
  files: AssetFile[],
  organizationId: string,
  userId: string,
  isBankOrganization: boolean
): Promise<ProcessChecklistResult> {
  const result: ProcessChecklistResult = {
    itemsCompleted: 0,
    evidenceCreated: 0,
    errors: [],
  };

  try {
    // Check if there's already a completion for this checklist and asset
    const existingCompletionsResult = await checklistCompletionService.getCompletionsForAsset(asset.id!);
    let existingCompletion: ChecklistCompletion | null = null;

    if (existingCompletionsResult.success && existingCompletionsResult.data) {
      existingCompletion = existingCompletionsResult.data.find(
        c => c.checklistId === checklist.id && c.assetId === asset.id
      ) || null;
    }

    // Create or update completion
    let completion: ChecklistCompletion;
    if (existingCompletion) {
      completion = existingCompletion;
    } else {
      completion = {
        checklistId: checklist.id!,
        checklistName: checklist.name,
        assetId: asset.id,
        assetName: asset.assetName,
        locationId: asset.locationId,
        variableValues: {},
        itemResults: {},
        completedBy: 'AUTO',
        completedByName: 'AUTO',
        completedAt: new Date(),
      };
    }

    // Initialize itemResults if not present
    if (!completion.itemResults) {
      completion.itemResults = {};
    }

    // Process each checklist item
    for (const item of checklist.items || []) {
      if (!item.evidenceDefinitions || item.evidenceDefinitions.length === 0) {
        continue; // Skip items without evidence definitions
      }

      // For each evidence definition, try to find matching documents
      for (const evidenceDef of item.evidenceDefinitions) {
        // Find files that might match this evidence category
        // For now, we'll process all files and let AI determine if they match
        // In a more sophisticated implementation, we could use file names, metadata, etc.

        // Check if we already have evidence for this item
        const itemResultId = `${completion.id || 'new'}-${item.text}`;
        const existingEvidenceResult = await checklistEvidenceService.getEvidenceByItemResult(
          completion.id || '',
          item.text
        );

        if (existingEvidenceResult.success && existingEvidenceResult.data && existingEvidenceResult.data.length > 0) {
          // Evidence already exists, skip
          continue;
        }

        // Try to extract field values from files
        // For now, we'll process the first file that seems relevant
        // In a production system, you'd want more sophisticated matching
        if (files.length > 0 && evidenceDef.fieldDefinitions && evidenceDef.fieldDefinitions.length > 0) {
          // Note: We need File objects, but we have AssetFile objects with URLs
          // For auto-completion, we might need to:
          // 1. Download files temporarily
          // 2. Or store file content during upload
          // 3. Or use a different approach
          
          // For now, we'll mark the item as needing review if evidence definitions exist
          // The user can manually complete it by uploading files in the UI
          
          // We could enhance this by:
          // - Storing file content during upload
          // - Using file metadata to match evidence categories
          // - Implementing a document classification service
        }

        // If required evidence is found, mark item as complete
        // For now, we'll create a placeholder that indicates auto-completion attempted
        if (!completion.itemResults[item.text]) {
          completion.itemResults[item.text] = {
            status: 'NEEDS_REVIEW', // Start as needs review, user can verify
            findingText: 'Auto-completion attempted. Please review evidence.',
            updatedBy: userId,
            updatedAt: new Date(),
          };
        }
      }
    }

    // Determine completion status
    const totalItems = checklist.items?.length || 0;
    const completedItems = Object.values(completion.itemResults).filter(
      r => r.status === 'PASS' || r.status === 'NA'
    ).length;

    // Set status based on completion
    if (completedItems === totalItems && totalItems > 0) {
      completion.status = 'complete';
      completion.completedAt = new Date();
    } else {
      // Some items incomplete - set to paused for auto-completed checklists
      completion.status = 'paused';
    }

    // Save or update completion
    if (existingCompletion && existingCompletion.id) {
      const updateResult = await checklistCompletionService.updateCompletion(
        existingCompletion.id,
        {
          itemResults: completion.itemResults,
          status: completion.status,
          completedAt: completion.completedAt,
          updatedAt: new Date(),
        }
      );

      if (updateResult.success) {
        result.itemsCompleted = Object.keys(completion.itemResults).length;
      } else {
        result.errors?.push(`Failed to update completion: ${updateResult.error}`);
      }
    } else {
      const createResult = await checklistCompletionService.addCompletion({
        ...completion,
        status: completion.status,
      });

      if (createResult.success && createResult.id) {
        result.itemsCompleted = Object.keys(completion.itemResults).length;
      } else {
        result.errors?.push(`Failed to create completion: ${createResult.error}`);
      }
    }

    return result;
  } catch (error: any) {
    console.error('Error processing checklist:', error);
    result.errors?.push(`Error: ${error.message}`);
    return result;
  }
}

/**
 * Enhanced version that processes files directly (when File objects are available)
 * This is called from the file upload handler
 * 
 * @param fileIdMap - Map of File.name -> AssetFile.id for uploaded files
 */
export async function autoCompleteChecklistsForAssetWithFiles(
  assetId: string,
  organizationId: string,
  organization: Organization,
  userId: string,
  uploadedFiles: File[],
  fileIdMap?: Map<string, string> // File.name -> AssetFile.id
): Promise<AutoCompleteResult> {
  const result: AutoCompleteResult = {
    success: true,
    checklistsProcessed: 0,
    itemsCompleted: 0,
    evidenceCreated: 0,
    factsExtracted: 0,
    errors: [],
  };

  try {
    // 1. Get the asset
    const assetResult = await assetService.getAsset(assetId);
    if (!assetResult.success || !assetResult.data) {
      result.success = false;
      result.errors?.push(`Failed to get asset: ${assetResult.error}`);
      return result;
    }

    const asset = assetResult.data;
    const organizationType = getIndustryType(organization);
    const isBankOrganization = organizationType === 'Bank';

    // 2. Extract field values from uploaded files using checklist field definitions
    // Get field definitions from checklists for this asset
    const fieldsResult = await getRequiredFieldsForAssetFacts(assetId, organizationId);
    
    if (fieldsResult.success && fieldsResult.requiredFields && fieldsResult.requiredFields.length > 0) {
      // Process files sequentially to avoid rate limiting
      for (let i = 0; i < uploadedFiles.length; i++) {
        const file = uploadedFiles[i];
        try {
          // Add delay between files to avoid rate limiting (429 errors)
          // Wait 3 seconds between files to respect rate limits (increased to avoid 429 errors)
          if (i > 0) {
            await new Promise(resolve => setTimeout(resolve, 3000));
          }

          const fieldResult = await extractFieldValuesFromDocument(file, fieldsResult.requiredFields!);
          if (fieldResult.fieldValues && Object.keys(fieldResult.fieldValues).length > 0) {
            const factsToSave: Record<string, { value: any; source: 'EXTRACTED'; extractedFrom?: string; confidence?: 'high' | 'medium' | 'low' }> = {};
            
            Object.entries(fieldResult.fieldValues).forEach(([key, value]) => {
              if (value !== null && value !== undefined) {
                factsToSave[key] = {
                  value,
                  source: 'EXTRACTED',
                  extractedFrom: file.name,
                  confidence: fieldResult.confidence,
                };
              }
            });

            if (Object.keys(factsToSave).length > 0) {
              await assetFactsService.setFacts(assetId, factsToSave, organizationId);
              result.factsExtracted += Object.keys(factsToSave).length;
            }
          }
        } catch (error: any) {
          console.error('Error extracting field values from file:', error);
          // Don't fail the entire process if one file fails
          if (error.message?.includes('429') || error.message?.includes('Resource exhausted')) {
            result.errors?.push(`Rate limit reached while processing ${file.name}. Please try again later.`);
            // Stop processing remaining files if we hit rate limit
            break;
          } else {
            result.errors?.push(`Failed to extract field values from ${file.name}: ${error.message}`);
          }
        }
      }
    } else {
      // No field definitions found - this is OK, just means no checklists are configured yet
      console.log('No field definitions found for this asset. Checklists may not be configured with evidence fields yet.');
    }

    // 3. Get all checklists for this asset
    const checklistsResult = await checklistService.getAllChecklists(organizationId);
    if (!checklistsResult.success || !checklistsResult.data) {
      result.success = false;
      result.errors?.push(`Failed to get checklists: ${checklistsResult.error}`);
      return result;
    }

    // Filter checklists that match this asset
    const relevantChecklists = checklistsResult.data.filter(checklist => {
      if (checklist.assetTypeIds && checklist.assetTypeIds.length > 0) {
        if (!asset.assetTypeId || !checklist.assetTypeIds.includes(asset.assetTypeId)) {
          return false;
        }
      }

      if (checklist.locationIds && checklist.locationIds.length > 0) {
        if (!asset.locationId || !checklist.locationIds.includes(asset.locationId)) {
          return false;
        }
      }

      return true;
    });

    if (relevantChecklists.length === 0) {
      result.errors?.push('No relevant checklists found for this asset');
      return result;
    }

    // 4. Process each checklist with the uploaded files
    for (const checklist of relevantChecklists) {
      try {
        const checklistResult = await processChecklistWithFiles(
          checklist,
          asset,
          uploadedFiles,
          organizationId,
          userId,
          isBankOrganization,
          organization,
          fileIdMap
        );

        result.checklistsProcessed++;
        result.itemsCompleted += checklistResult.itemsCompleted;
        result.evidenceCreated += checklistResult.evidenceCreated;
        if (checklistResult.errors) {
          result.errors?.push(...checklistResult.errors);
        }
      } catch (error: any) {
        console.error(`Error processing checklist ${checklist.id}:`, error);
        result.errors?.push(`Failed to process checklist "${checklist.name}": ${error.message}`);
      }
    }

    return result;
  } catch (error: any) {
    console.error('Error auto-completing checklists:', error);
    result.success = false;
    result.errors?.push(`Fatal error: ${error.message}`);
    return result;
  }
}

/**
 * Process a checklist with File objects (from upload)
 */
async function processChecklistWithFiles(
  checklist: Checklist,
  asset: Asset,
  files: File[],
  organizationId: string,
  userId: string,
  isBankOrganization: boolean,
  organization: Organization,
  fileIdMap?: Map<string, string> // File.name -> AssetFile.id
): Promise<ProcessChecklistResult> {
  const result: ProcessChecklistResult = {
    itemsCompleted: 0,
    evidenceCreated: 0,
    errors: [],
  };

  try {
    // Check if there's already a completion
    const existingCompletionsResult = await checklistCompletionService.getCompletionsForAsset(asset.id!);
    let existingCompletion: ChecklistCompletion | null = null;

    if (existingCompletionsResult.success && existingCompletionsResult.data) {
      existingCompletion = existingCompletionsResult.data.find(
        c => c.checklistId === checklist.id && c.assetId === asset.id
      ) || null;
    }

    // Create or update completion
    let completion: ChecklistCompletion;
    if (existingCompletion) {
      completion = existingCompletion;
    } else {
      completion = {
        checklistId: checklist.id!,
        checklistName: checklist.name,
        assetId: asset.id,
        assetName: asset.assetName,
        locationId: asset.locationId,
        variableValues: {},
        itemResults: {},
        completedBy: 'AUTO',
        completedByName: 'AUTO',
        completedAt: new Date(),
      };
    }

    if (!completion.itemResults) {
      completion.itemResults = {};
    }

    // Get asset facts for fact-based evaluation
    const factsResult = await assetFactsService.getAssetFacts(asset.id!);
    const facts = factsResult.success && factsResult.data ? factsResult.data.facts : {};

    // Process each checklist item (fields come from evidenceDefinitions now)
    for (const item of checklist.items || []) {
      // Step 1: Extract all evidence fields from asset facts (EXACT key matching)
      const extractedFields = extractEvidenceFieldsForItem(item, facts);
      
      // Step 2: Check auto-complete rules (keep for backward compatibility)
      const autoComplete = evaluateAutoCompleteRules(item, facts);
      if (autoComplete && autoComplete.status) {
        if (!completion.itemResults[item.text]) {
          completion.itemResults[item.text] = {
            status: autoComplete.status,
            findingText: autoComplete.reason || 'Auto-completed by rule',
            updatedBy: 'AUTO',
            updatedAt: new Date(),
          };
          if (autoComplete.status === 'PASS' || autoComplete.status === 'NA') {
            result.itemsCompleted++;
          }
        }
        // Continue to AI evaluation even if auto-completed (AI can override if needed)
      }
      
      // Step 3: Use AI to evaluate item status based on evidence fields
      // Only evaluate if item has evidence definitions (AI needs evidence context)
      if (item.evidenceDefinitions && item.evidenceDefinitions.length > 0) {
        try {
          const aiResult = await evaluateChecklistItemWithAI(item, extractedFields, facts);
          
          // Use AI result if we don't have a result yet, or if AI provides a more specific evaluation
          if (!completion.itemResults[item.text] || aiResult.status !== 'NEEDS_REVIEW') {
            // Validate required fields to ensure completeness
            const evidenceFields: Record<string, any> = {};
            Object.values(extractedFields).forEach(categoryFields => {
              Object.entries(categoryFields).forEach(([key, fieldValue]) => {
                if (fieldValue.value !== null && fieldValue.value !== undefined && fieldValue.value !== '') {
                  evidenceFields[key] = fieldValue.value;
                }
              });
            });
            
            const validation = validateItemCompletion(
              item,
              completion.itemResults[item.text],
              facts,
              evidenceFields
            );
            
            // Determine final status: combine AI result with validation
            let finalStatus = aiResult.status;
            if (validation.status === 'INCOMPLETE' && aiResult.status === 'PASS') {
              // AI says PASS but validation says incomplete - trust validation
              finalStatus = 'NEEDS_REVIEW';
            } else if (validation.status === 'COMPLETE' && aiResult.status === 'NEEDS_REVIEW') {
              // Validation says complete but AI says needs review - trust AI
              finalStatus = 'NEEDS_REVIEW';
            }
            
            completion.itemResults[item.text] = {
              status: finalStatus,
              findingText: aiResult.findingText,
              updatedBy: 'AUTO',
              updatedAt: new Date(),
              // Store AI reasoning for transparency (if ChecklistItemResult supports it)
              ...(aiResult.reasoning && { notes: `AI Reasoning: ${aiResult.reasoning}` }),
            };
            
            if (finalStatus === 'PASS' || finalStatus === 'NA') {
              result.itemsCompleted++;
            }
          }
        } catch (aiError: any) {
          console.error(`[AI Evaluation] Error evaluating item "${item.text}":`, aiError);
          // Fall back to hardcoded evaluation if AI fails
          const factBasedResult = evaluateItemBasedOnFacts(item, facts);
          if (factBasedResult && !completion.itemResults[item.text]) {
            completion.itemResults[item.text] = {
              status: factBasedResult.status,
              findingText: `${factBasedResult.findingText} (Fallback: AI evaluation failed)`,
              updatedBy: 'AUTO',
              updatedAt: new Date(),
            };
            if (factBasedResult.status === 'PASS' || factBasedResult.status === 'NA') {
              result.itemsCompleted++;
            }
          }
        }
      } else {
        // No evidence definitions - fall back to hardcoded evaluation
        const factBasedResult = evaluateItemBasedOnFacts(item, facts);
        if (factBasedResult && !completion.itemResults[item.text]) {
          completion.itemResults[item.text] = {
            status: factBasedResult.status,
            findingText: factBasedResult.findingText,
            updatedBy: 'AUTO',
            updatedAt: new Date(),
          };
          if (factBasedResult.status === 'PASS' || factBasedResult.status === 'NA') {
            result.itemsCompleted++;
          }
        }
      }

      // Step 4: Process evidence from files (extract new evidence and create evidence records)
      // This happens after AI evaluation to:
      // - Extract additional fields from documents that weren't in asset facts
      // - Create evidence records for audit trail
      // - Update asset facts with newly extracted values
      if (!item.evidenceDefinitions || item.evidenceDefinitions.length === 0) {
        continue;
      }

      // Process each evidence definition (fields come from evidenceDefinitions.fieldDefinitions now)
      for (const evidenceDef of item.evidenceDefinitions) {
        // Get fields from evidence definition, including fileNameKey/fileNameKeys
        const fieldsForThisEvidence = (evidenceDef.fieldDefinitions || []).map(fieldDef => ({
          key: fieldDef.key,
          label: fieldDef.label,
          type: fieldDef.type,
          fileNameKey: fieldDef.fileNameKey, // Legacy support
          fileNameKeys: getFileNameKeys(fieldDef), // New: array of keys
          extractionHints: fieldDef.extractionHints,
        }));

        if (fieldsForThisEvidence.length === 0) {
          continue; // No fields to extract
        }

        // Separate fields with and without fileNameKeys
        const fieldsWithFileNameKeys = fieldsForThisEvidence.filter(f => f.fileNameKeys.length > 0);
        const fieldsWithoutFileNameKeys = fieldsForThisEvidence.filter(f => f.fileNameKeys.length === 0);

        // Collect all unique fileNameKeys from all fields (flatten array of arrays)
        const allFileNameKeys = new Set<string>();
        fieldsWithFileNameKeys.forEach(field => {
          field.fileNameKeys.forEach(key => allFileNameKeys.add(key));
        });
        
        let evidenceCreated = false;

        // Process files that match fileNameKey requirements
        // Strategy: 
        // 1. For fields with fileNameKeys, only process files whose names match any of those keys
        // 2. For fields without fileNameKeys, process all files
        const filesToProcess = new Set<File>();
        
        // Add files that match any fileNameKey
        if (allFileNameKeys.size > 0) {
          for (const file of files) {
            if (fileNameMatchesAnyKey(file.name, Array.from(allFileNameKeys))) {
              filesToProcess.add(file);
            }
          }
        }
        
        // If there are fields without fileNameKeys, we need to process all files for those
        if (fieldsWithoutFileNameKeys.length > 0) {
          files.forEach(f => filesToProcess.add(f));
        }

        // Process each matching file
        for (const file of filesToProcess) {
          try {
            // Add delay between files to avoid rate limiting
            const fileArray = Array.from(filesToProcess);
            const fileIndex = fileArray.indexOf(file);
            if (fileIndex > 0) {
              await new Promise(resolve => setTimeout(resolve, 3000));
            }
            
            // Also add delay between evidence definitions to avoid too many calls
            const evidenceIndex = item.evidenceDefinitions?.indexOf(evidenceDef) || 0;
            if (evidenceIndex > 0) {
              await new Promise(resolve => setTimeout(resolve, 2000));
            }

            // Determine which fields to extract from this file
            const fieldsToExtractFromThisFile: typeof fieldsForThisEvidence = [];
            
            // Add fields without fileNameKeys (they apply to all files)
            fieldsToExtractFromThisFile.push(...fieldsWithoutFileNameKeys);
            
            // Add fields with fileNameKeys only if this file matches any of their keys
            for (const field of fieldsWithFileNameKeys) {
              if (fileNameMatchesAnyKey(file.name, field.fileNameKeys)) {
                fieldsToExtractFromThisFile.push(field);
              }
            }

            // Extract field values if any fields match this file
            if (fieldsToExtractFromThisFile.length > 0) {
              const fieldResult = await extractFieldValuesFromDocument(file, fieldsToExtractFromThisFile);

              if (fieldResult.fieldValues && Object.keys(fieldResult.fieldValues).length > 0) {
                // Found matching evidence - create evidence record
                // First, ensure completion is saved to get an ID
                let completionId = completion.id;
                if (!completionId) {
                  // Set initial status - will be updated later with full summary
                  const tempCompletion = {
                    ...completion,
                    status: 'paused' as const, // Default to paused for auto-completed checklists
                  };
                  const createResult = await checklistCompletionService.addCompletion(tempCompletion);
                  if (createResult.success && createResult.id) {
                    completionId = createResult.id;
                    completion.id = completionId;
                  } else {
                    result.errors?.push(`Failed to create completion: ${createResult.error}`);
                    continue;
                  }
                }

                // Get fileId from map
                const fileId = fileIdMap?.get(file.name);

                // Create evidence record
                const evidenceResult = await checklistEvidenceService.addEvidence(
                  {
                    checklistCompletionId: completionId,
                    itemText: item.text,
                    evidenceType: 'FILE',
                    evidenceName: evidenceDef.evidenceName || (evidenceDef as any).categoryCode || 'Unknown Evidence',
                    source: 'UPLOAD',
                    fileId: fileId,
                    documentName: file.name,
                    organizationId: organizationId,
                    addedByUserId: userId,
                    // Store extracted field values in notes for now (could be enhanced to store in a separate structure)
                    notes: `Auto-extracted fields: ${JSON.stringify(fieldResult.fieldValues)}`,
                  },
                  organization
                );

                if (evidenceResult.success) {
                  // Extract field values from evidence for validation
                  const evidenceFields: Record<string, any> = {};
                  if (fieldResult.fieldValues) {
                    Object.assign(evidenceFields, fieldResult.fieldValues);
                  }
                  
                  // Validate item completion after evidence is created
                  const itemResult = completion.itemResults[item.text];
                  const validation = validateItemCompletion(
                    item,
                    itemResult,
                    facts,
                    evidenceFields
                  );
                  
                  // Mark item as complete if required evidence is found AND validation passes
                  if (evidenceDef.required) {
                    if (!completion.itemResults[item.text]) {
                      completion.itemResults[item.text] = {
                        status: validation.status === 'COMPLETE' ? 'PASS' : 'NEEDS_REVIEW',
                        findingText: validation.status === 'COMPLETE' 
                          ? `Auto-completed: Evidence found in ${file.name}`
                          : `Evidence found but missing required fields: ${validation.missingFields.join(', ')}`,
                        updatedBy: 'AUTO',
                        updatedAt: new Date(),
                      };
                      if (validation.status === 'COMPLETE') {
                        result.itemsCompleted++;
                      }
                    }
                    evidenceCreated = true;
                    result.evidenceCreated++;
                    break; // Found required evidence, move to next evidence definition
                  } else {
                    // Optional evidence found
                    result.evidenceCreated++;
                  }
                } else {
                  result.errors?.push(`Failed to create evidence: ${evidenceResult.error}`);
                }
              }
            }
          } catch (error: any) {
            console.error(`Error processing file ${file.name} for evidence:`, error);
            // Handle rate limiting gracefully
            if (error.message?.includes('429') || error.message?.includes('Resource exhausted')) {
              result.errors?.push(`Rate limit reached while processing ${file.name}. Please try again later.`);
              // Stop processing remaining files if we hit rate limit
              break;
            } else {
              result.errors?.push(`Error processing ${file.name}: ${error.message}`);
            }
          }
        }

        // If required evidence not found, mark as needs review
        if (evidenceDef.required && !evidenceCreated) {
          if (!completion.itemResults[item.text]) {
            completion.itemResults[item.text] = {
              status: 'NEEDS_REVIEW',
              findingText: 'Required evidence not found in uploaded documents',
              updatedBy: userId,
              updatedAt: new Date(),
            };
          }
        }
      }
    }

    // Determine completion status and generate summary
    const totalItems = checklist.items?.length || 0;
    const completedItems = Object.values(completion.itemResults).filter(
      r => r.status === 'PASS' || r.status === 'NA'
    ).length;

    // Generate completion summary
    let completionSummary: {
      totalItems: number;
      completedItems: number;
      incompleteItems: number;
      missingRequiredFields: Array<{ itemText: string; missingFields: string[] }>;
      missingRequiredEvidence: Array<{ itemText: string; evidenceCategory: string }>;
      needsReviewItems: string[];
    } | undefined = undefined;

    try {
      const missingRequiredFields: Array<{ itemText: string; missingFields: string[] }> = [];
      const missingRequiredEvidence: Array<{ itemText: string; evidenceCategory: string }> = [];
      const needsReviewItems: string[] = [];

      for (const item of checklist.items || []) {
        const itemResult = completion.itemResults[item.text];
        const validation = validateItemCompletion(item, itemResult, facts);

        if (validation.missingFields.length > 0) {
          missingRequiredFields.push({
            itemText: item.text,
            missingFields: validation.missingFields,
          });
        }

        if (item.evidenceDefinitions) {
          for (const evidenceDef of item.evidenceDefinitions) {
            if (evidenceDef.required && validation.status === 'INCOMPLETE') {
              missingRequiredEvidence.push({
                itemText: item.text,
                evidenceCategory: evidenceDef.evidenceName || (evidenceDef as any).categoryCode || 'Unknown Evidence',
              });
            }
          }
        }

        if (validation.status === 'NEEDS_REVIEW' || itemResult?.status === 'NEEDS_REVIEW') {
          needsReviewItems.push(item.text);
        }
      }

      completionSummary = {
        totalItems,
        completedItems,
        incompleteItems: totalItems - completedItems,
        missingRequiredFields,
        missingRequiredEvidence,
        needsReviewItems,
      };
    } catch (error) {
      console.error('Error generating completion summary:', error);
    }

    // Set status based on completion
    if (completedItems === totalItems && totalItems > 0) {
      completion.status = 'complete';
      completion.completedAt = new Date();
    } else {
      // Some items incomplete - set to paused for auto-completed checklists
      completion.status = 'paused';
      completion.completionSummary = completionSummary;
    }

    // Save or update completion (update itemResults if completion already exists)
    if (completion.id) {
      const updateResult = await checklistCompletionService.updateCompletion(
        completion.id,
        {
          itemResults: completion.itemResults,
          status: completion.status,
          completedAt: completion.completedAt,
          completionSummary: completion.completionSummary,
          updatedAt: new Date(),
        }
      );

      if (!updateResult.success) {
        result.errors?.push(`Failed to update completion: ${updateResult.error}`);
      }
    } else if (existingCompletion && existingCompletion.id) {
      const updateResult = await checklistCompletionService.updateCompletion(
        existingCompletion.id,
        {
          itemResults: completion.itemResults,
          status: completion.status,
          completedAt: completion.completedAt,
          completionSummary: completion.completionSummary,
          updatedAt: new Date(),
        }
      );

      if (!updateResult.success) {
        result.errors?.push(`Failed to update completion: ${updateResult.error}`);
      }
    } else {
      const createResult = await checklistCompletionService.addCompletion({
        ...completion,
        completionSummary: completion.completionSummary,
      });

      if (!createResult.success) {
        result.errors?.push(`Failed to create completion: ${createResult.error}`);
      }
    }

    return result;
  } catch (error: any) {
    console.error('Error processing checklist with files:', error);
    result.errors?.push(`Error: ${error.message}`);
    return result;
  }
}

